import { useCallback, useEffect, useRef, useState } from "react";
import { getSavedSurchargeMode } from "shared/helpers/utilities";

/**
 * Keeps the saved surcharge mode in sync with the server (KP-2492).
 *
 * The mode decides whether wallet fee rows expose real domestic values or render "Not Applicable",
 * so it has to be the one the server holds right now — not the copy the page happened to load with.
 * It is seeded from the detail response the parent already has, and re-read through
 * `readSurchargeConfig` whenever the caller knows it may have moved: saving a fee record refreshes
 * the fee list only, while the surcharge could have been changed elsewhere in the meantime.
 *
 * @param surchargeConfig `surcharge_config` from the merchant/partner detail the parent loaded
 * @param readSurchargeConfig reads the current `surcharge_config` from the server; held in a ref, so
 * an inline arrow is fine
 * @returns the mode to render with, and the refresher to call once a save has gone through
 */
const useSavedSurchargeMode = (
  surchargeConfig: any,
  readSurchargeConfig: () => Promise<any>
) => {
  const [savedSurchargeMode, setSavedSurchargeMode] = useState<number>(() =>
    getSavedSurchargeMode(surchargeConfig)
  );

  // Held in a ref so the refresher stays stable even when the caller passes an inline arrow.
  const readRef = useRef(readSurchargeConfig);
  readRef.current = readSurchargeConfig;

  // The parent reloading its own detail is just as authoritative, so follow it back.
  useEffect(() => {
    setSavedSurchargeMode(getSavedSurchargeMode(surchargeConfig));
  }, [surchargeConfig]);

  /** Re-read the saved mode from the server and render against it. */
  const refreshSavedSurchargeMode = useCallback(async () => {
    try {
      setSavedSurchargeMode(getSavedSurchargeMode(await readRef.current()));
    } catch {
      // A failed read keeps the last known mode: the rows carry on showing what the page already
      // believed rather than silently falling back to No Surcharge.
    }
  }, []);

  return { savedSurchargeMode, refreshSavedSurchargeMode };
};

export default useSavedSurchargeMode;
